import React from 'react'
import { Grid } from '@the-platform-group/component-library/Layout'
import { Typography, Divider, Chip } from '@the-platform-group/component-library'
import {
  Email as EmailIcon,
  Phone as PhoneIcon,
  Work as WorkIcon,
  School as SchoolIcon,
  MenuBook as MenuBookIcon,
} from '@the-platform-group/component-library/Icons'
import useContactLookupState from '../hooks/useContactLookupState'
import DetailsCard from './DetailsCard'
import EmailGrader from './EmailGrader'
import PhoneGrader from './PhoneGrader'

/**
 * Rocket Reach Details Component that displays the details of the contact from the cache (lookup data) or search data
 * @param {Object} row - The row data that contains search API data
 * @returns {React.Component} - The RocketReachDetails component that displays the details of the contact
 */
const RocketReachDetails = ({ row }) => {
  const { hasLookupData, lookupData } = useContactLookupState(row.id)

  // Use cached lookup data if available, otherwise use search data
  const displayData = lookupData || row

  return (
    <Grid container direction="row" sx={{ bgcolor: 'secondary.light' }}>
      <Grid item container justifyContent="flex-end" xs={12} sx={{ px: 1, pt: 1 }} spacing={1}>
        {displayData.requiresStatusCheck && (
          <Grid item>
            <Chip
              label="In progress"
              size="small"
              sx={{
                bgcolor: 'warning.background',
                border: '1px solid',
                borderColor: 'warning.contrastText',
                color: 'warning.contrastText',
              }}
            />
          </Grid>
        )}
        <Grid item>
          <Chip
            label={hasLookupData ? 'Lookup Data' : 'Search Data'}
            size="small"
            color={hasLookupData ? 'primary' : 'default'}
            variant={hasLookupData ? 'default' : 'outlined'}
          />
        </Grid>
      </Grid>
      <Grid item xs={12} sm={6} sx={{ p: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
          Details
        </Typography>
      </Grid>
      <Grid item container xs={12} sm={6} sx={{ p: 1 }} spacing={2} alignItems="center">
        <Grid item>
          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
            Other Contact Options
          </Typography>
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <Divider />
      </Grid>

      <Grid item container xs={12} sm={6} sx={{ p: 1, gap: 2 }}>
        <DetailsCard
          startIcon={<WorkIcon width={16} height={16} />}
          title="Work"
          values={displayData.jobHistory}
          getValue={jobObject => `${jobObject?.title} @ ${jobObject?.companyName}`}
        />
        <DetailsCard
          startIcon={<SchoolIcon width={16} height={16} />}
          title="Education"
          values={displayData.education}
          getValue={educationObject =>
            `${
              educationObject?.start && educationObject?.end
                ? `(${educationObject?.start}-${educationObject?.end}) `
                : ''
            }${educationObject?.degree ? `, ${educationObject?.degree} at ` : ''}${
              educationObject?.school ? educationObject?.school : ''
            }`
          }
        />
        <DetailsCard
          startIcon={<MenuBookIcon width={16} height={16} />}
          title="Skills"
          values={displayData.skills?.join(', ')}
        />
      </Grid>

      <Grid item xs={12} sm={6} sx={{ p: 1 }}>
        {displayData.emails.map((emailObject, index) => (
          <DetailsCard
            key={`email-${index}`}
            startIcon={<EmailIcon width={16} height={16} />}
            values={[emailObject.email]}
            endIcon={emailObject?.grade && <EmailGrader email={emailObject} />}
          />
        ))}
        {displayData.phones.map((phoneObject, index) => (
          <DetailsCard
            key={`phone-${index}`}
            startIcon={<PhoneIcon width={16} height={16} />}
            values={[phoneObject.number]}
            endIcon={phoneObject?.type !== null && <PhoneGrader phone={phoneObject} />}
          />
        ))}
      </Grid>
    </Grid>
  )
}

export default RocketReachDetails
